import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    ChevronLeft,
    CircleAlert,
    ClipboardCheck,
    LoaderCircle,
    PencilLine,
    Plus,
    ShieldCheck,
    Users,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaArrowRightLong } from 'react-icons/fa6'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './LeavePolicyListScreen.module.css'

const LEAVE_TYPE_COLORS = {
    CL: { bg: '#E3F2FD', text: '#1565C0' },
    SL: { bg: '#FFF3E0', text: '#E65100' },
    PL: { bg: '#E8F5E9', text: '#2E7D32' },
    CO: { bg: '#F3E5F5', text: '#6A1B9A' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const fmtDate = (value) => {
    if (!value) return '—'
    const parsed = value.includes(' ') ? value.replace(' ', 'T') : value
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
}

const LeavePolicyListScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [policyData, setPolicyData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const fetchLeavePolicies = useCallback(
        async ({ silent = false } = {}) => {
            if (!authCtx?.token || !activeStore?.storeIdentifier) {
                setErrorMessage('Please select a store first to continue.')
                setIsLoading(false)
                return
            }

            if (silent) {
                setIsRefreshing(true)
            } else {
                setIsLoading(true)
            }
            setErrorMessage('')

            try {
                const response = await fetch(BASE_URL_API + 'getStoreLeavePolicyList', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                    }),
                })

                if (!response.ok) {
                    setErrorMessage('Failed to load leave policies.')
                    toast.error('Failed to load leave policies.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    if (!respo?.response?.isConfigured) {
                        navigate('/payroll/setup/leave-policy/setup', {
                            replace: true,
                            state: { preselectTypes: respo?.response?.missingTypes || [] },
                        })
                        return
                    }
                    setPolicyData(respo.response)
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                if (respo?.errorCode === 'VAL_001') {
                    setErrorMessage('Store not selected. Please choose a store and try again.')
                } else {
                    setErrorMessage(respo?.message || 'Failed to load leave policies.')
                }
                toast.error(respo?.message || 'Failed to load leave policies.')
            } catch (error) {
                console.error('getStoreLeavePolicyList error:', error)
                setErrorMessage('Network error. Please check your connection.')
                toast.error('Network error. Please check your connection.')
            } finally {
                setIsLoading(false)
                setIsRefreshing(false)
            }
        },
        [activeStore?.storeIdentifier, authCtx, navigate]
    )

    useEffect(() => {
        fetchLeavePolicies()
    }, [fetchLeavePolicies])

    useEffect(() => {
        if (location?.state?.refreshLeavePolicies) {
            fetchLeavePolicies({ silent: true })
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [fetchLeavePolicies, location.pathname, location.state, navigate])

    const totalConfiguredTypes = Number(policyData?.configuredTypes?.length || 0)
    const totalAnnualLeave = Number(policyData?.totalAnnualLeave || 0)
    const missingTypes = policyData?.missingTypes || []
    const policies = policyData?.policies || []
    const hasAnyUninitializedPolicy = policies.some(
        (policy) => Number(policy?.staffWithBalanceCount || 0) === 0
    )

    const typeBadgeStyle = useMemo(() => LEAVE_TYPE_COLORS, [])

    const handleBack = () => navigate(-1)

    const handleAddMissingTypes = () => {
        navigate('/payroll/setup/leave-policy/setup', {
            state: { preselectTypes: missingTypes },
        })
    }

    const handleConfigureNow = () => {
        handleAddMissingTypes()
    }

    const handleEditPolicy = (leaveType) => {
        navigate('/payroll/setup/leave-policy/edit', {
            state: {
                currentPolicies: policies,
                selectedLeaveType: leaveType,
            },
        })
    }

    const handleInitializeLeave = () => {
        navigate('/payroll/setup/leave-balance-init')
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={handleBack} type="button">
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Leave Policies</h1>
                    <p className={styles.headerSub}>View configured leave rules for this salon</p>
                </div>

                {missingTypes.length > 0 && (
                    <button className={styles.addBtn} type="button" onClick={handleAddMissingTypes}>
                        <Plus size={14} />
                        Add Type
                    </button>
                )}
            </div>

            {isLoading ? (
                <div className={styles.stateCard}>
                    <LoaderCircle className={styles.spinIcon} size={18} />
                    <span>Loading leave policies...</span>
                </div>
            ) : errorMessage ? (
                <div className={`${styles.stateCard} ${styles.stateError}`}>
                    <CircleAlert size={16} />
                    <span>{errorMessage}</span>
                </div>
            ) : (
                <>
                    {isRefreshing && (
                        <div className={styles.refreshingNote}>
                            <LoaderCircle className={styles.spinIcon} size={14} />
                            <span>Refreshing latest leave policy details...</span>
                        </div>
                    )}

                    <div className={styles.summaryCard}>
                        <div className={styles.summaryTextWrap}>
                            <p className={styles.summaryTitle}>Policy Overview</p>
                            <p className={styles.summaryText}>
                                Total annual leave: <strong>{totalAnnualLeave.toFixed(1)} days</strong> across{' '}
                                <strong>{totalConfiguredTypes} type(s)</strong>
                            </p>
                        </div>
                        <ShieldCheck size={18} className={styles.summaryIcon} />
                    </div>

                    {missingTypes.length > 0 && (
                        <div className={styles.missingBanner}>
                            <div className={styles.missingText}>
                                <AlertTriangle size={16} />
                                <span>
                                    <strong>{missingTypes.join(', ')}</strong> not configured yet.
                                </span>
                            </div>
                            <button
                                className={styles.missingActionBtn}
                                type="button"
                                onClick={handleConfigureNow}
                            >
                                Configure Now
                                <FaArrowRightLong size={12} />
                            </button>
                        </div>
                    )}

                    {hasAnyUninitializedPolicy && (
                        <div className={styles.initBanner}>
                            <div className={styles.initBannerText}>
                                <ClipboardCheck size={15} />
                                <span>Some leave types still have 0 staff balances initialized.</span>
                            </div>
                            <button type="button" className={styles.initActionBtn} onClick={handleInitializeLeave}>
                                Initialize Now
                                <FaArrowRightLong size={12} />
                            </button>
                        </div>
                    )}

                    <div className={styles.cardsWrap}>
                        {policies.map((policy) => {
                            const badgeColor = typeBadgeStyle[policy?.leaveType] || {
                                bg: '#E2E8F0',
                                text: '#334155',
                            }
                            const staffCount = Number(policy?.staffWithBalanceCount || 0)

                            return (
                                <div className={styles.policyCard} key={policy?.policyIdentifier || policy?.leaveType}>
                                    <div className={styles.policyHead}>
                                        <div className={styles.policyTitleWrap}>
                                            <h3 className={styles.policyTitle}>
                                                {policy?.leaveTypeLabel || policy?.leaveType || 'Leave Type'}
                                            </h3>
                                            <span
                                                className={styles.typeBadge}
                                                style={{
                                                    backgroundColor: badgeColor.bg,
                                                    color: badgeColor.text,
                                                }}
                                            >
                                                {policy?.leaveType || '—'}
                                            </span>
                                        </div>
                                        <span className={styles.entitlementChip}>
                                            {Number(policy?.annualEntitlement || 0).toFixed(1)} days/year
                                        </span>
                                    </div>

                                    <div className={styles.policyMeta}>
                                        <p className={styles.metaLine}>{policy?.accrualLabel || '—'}</p>
                                        <p className={styles.metaLine}>{policy?.carryForwardLabel || '—'}</p>
                                        <p className={styles.metaLine}>{policy?.encashmentLabel || '—'}</p>
                                    </div>

                                    <div className={styles.policyFooter}>
                                        <div className={styles.footerItem}>
                                            <CalendarClock size={14} />
                                            <span>Active from {fmtDate(policy?.effectiveFrom)}</span>
                                        </div>
                                        <div
                                            className={`${styles.footerItem} ${
                                                staffCount === 0 ? styles.footerMuted : ''
                                            }`}
                                        >
                                            <Users size={14} />
                                            <span>
                                                {staffCount > 0
                                                    ? `${staffCount} staff initialized`
                                                    : 'No staff initialized yet'}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.editBtn}
                                            onClick={() => handleEditPolicy(policy?.leaveType)}
                                        >
                                            <PencilLine size={13} />
                                            Edit
                                        </button>
                                        {staffCount === 0 && (
                                            <button
                                                type="button"
                                                className={styles.initInlineBtn}
                                                onClick={handleInitializeLeave}
                                            >
                                                <ClipboardCheck size={13} />
                                                Initialize Leave
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className={styles.bottomNote}>
                        <InfoRow />
                    </div>
                </>
            )}
        </div>
    )
}

const InfoRow = () => (
    <p className={styles.noteText}>
        <ArrowRight size={13} />
        To change entitlement or rules, create a new policy version with a new Effective From date.
    </p>
)

export default LeavePolicyListScreen